import { cn, tv } from "@utils/tw"
import { useMemo, useState } from "react"
import { FILM_FILTERS, FilmFilter, FilmFilterKey, FilmFilterOptions, FilmFilterValue } from "./program-filters"
import { FilmSearch } from "./useFilmSearch"
import { AnimatePresence, motion } from "motion/react"
import { Command } from "cmdk"
import { Icon } from "@components/ui/Icon"
import { Button } from "@components/ui/Button"
import { Drawer } from "@components/ui/Drawer"
import { Popover } from "@components/ui/Popover"

type Props = {
  as: "popover" | "drawer"
  close: () => void
  browse: FilmSearch
}

export function AllProgramFilters(props: Props) {
  const [activeFilter, setActiveFilter] = useState<FilmFilterKey | null>(null)
  const [values, setValues] = useState(() => {
    const values = {} as Record<string, any[]>
    for (const key of Object.keys(FILM_FILTERS) as FilmFilterKey[]) {
      const filter = FILM_FILTERS[key]
      values[key] = props.browse.filters.get(key) ?? filter.getDefault()
    }
    return values
  })

  const hasFilters = Object.values(values).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0
    } else {
      return !!value
    }
  })

  return (
    <div
      className={cn(
        "flex flex-col",
        props.as === "popover"
          ? "md:h-[calc(var(--radix-popover-content-available-height)-12px)] md:w-[390px]"
          : "h-[calc(100svh_-_64px)]",
      )}
    >
      {props.as === "drawer" ? (
        <Drawer.Title className="px-6 type-title-l pt-4 pb-5 border-b border-bg-mid">All Filters</Drawer.Title>
      ) : (
        <div className="type-ui-l py-4 px-4 border-b border-b-bg-low">All Filters</div>
      )}
      <div className="overflow-auto flex-1">
        {Object.entries(FILM_FILTERS).map(([key, filter]) => {
          return (
            <div key={key} className="border-t border-bg-low first:border-t-0">
              <FilterAccordion
                as={props.as}
                expanded={activeFilter === key}
                onToggle={() => {
                  if (activeFilter !== key) {
                    setActiveFilter(key as FilmFilterKey)
                  } else {
                    setActiveFilter(null)
                  }
                }}
                filterKey={key as FilmFilterKey}
                value={values[key]}
                browse={props.browse}
                onChange={(value) => {
                  setValues((prev) => ({
                    ...prev,
                    [key]: value,
                  }))
                }}
              />
            </div>
          )
        })}
      </div>
      <div className="border-t border-bg-mid flex items-center justify-evenly px-6 py-4 gap-4 md:px-3 md:py-3 md:gap-2 flex-none">
        <Button
          className="flex-1"
          variant="outline"
          disabled={!hasFilters}
          onClick={() => {
            props.browse.filters.setAll({} as any)
            props.close()
          }}
        >
          Clear All
        </Button>
        <Button
          className="flex-1"
          variant="primary"
          onClick={() => {
            props.browse.filters.setAll(values)
            props.close()
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

type FilterProps<K extends FilmFilterKey> = {
  expanded: boolean
  onToggle: () => void
  as: "popover" | "drawer"
  filterKey: K
  value: any
  onChange: (value: any) => void
  browse: FilmSearch
}

function FilterAccordion<K extends FilmFilterKey>(props: FilterProps<K>) {
  const spec = FILM_FILTERS[props.filterKey]
  const styles = accordionStyles(props)

  const store = useMemo(() => {
    return { value: props.value as any, setValue: props.onChange }
  }, [props.value])

  return (
    <div className={styles.base()}>
      <button className={styles.header()} onClick={() => props.onToggle()}>
        <span>{spec.name}</span>
        <Icon className={cn("transition-transform -rotate-90", props.expanded && "rotate-0")} name="chevronDown" />
      </button>
      <AnimatePresence>
        {props.expanded && (
          <motion.div
            className={styles.contents()}
            all-filters=""
            variants={{ open: { height: "auto" }, closed: { height: 0 } }}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <Command>
              <div className="border-t border-t-bg-low">
                <FilmFilter browse={props.browse} filterKey={props.filterKey} store={store} />
              </div>
            </Command>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const accordionStyles = tv({
  base: "flex flex-col",
  slots: {
    header: "flex items-center justify-between px-6 md:px-4 py-3 type-ui-m",
    contents: "overflow-hidden",
  },
  variants: {
    expanded: {
      true: {},
    },
  },
})
